import { useEffect, useState } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import ScrollToTop from '../../../components/feature/ScrollToTop';
import QuoteFormSection from '../../home/components/QuoteFormSection';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { useSEO } from '../../../utils/seo';

const HormigonDesactivadoPage = () => {
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hormipremium.com';

  useSEO({
    title: 'Hormigón Desactivado Estepona | Pavimentos Decorativos HORMI PREMIUM',
    description: 'Especialistas en hormigón desactivado en Estepona. Pavimentos con áridos vistos para jardines, caminos y zonas peatonales. Acabado natural y resistente.',
    keywords: 'hormigón desactivado Estepona, pavimento áridos vistos, hormigón lavado, suelos decorativos',
    canonical: `${siteUrl}/servicios/hormigon-desactivado`,
  });

  useEffect(() => {
    loadImages();
    setupScrollAnimations();
  }, []);

  const setupScrollAnimations = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  };

  const loadImages = async () => {
    try {
      const { data: files, error } = await supabase.storage
        .from('HormiPremium')
        .list('Dezactivado', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' },
        });

      if (error) {
        console.error('Error loading images:', error);
        setLoading(false);
        return;
      }

      if (files && files.length > 0) {
        const imageUrls = files
          .filter((file) => file.name !== '.emptyFolderPlaceholder' && file.id)
          .map((file) => {
            const { data } = supabase.storage
              .from('HormiPremium')
              .getPublicUrl(`Dezactivado/${file.name}`);
            return data.publicUrl;
          });

        setImages(imageUrls);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const features = [
    {
      icon: 'ri-leaf-line',
      title: 'Aspecto Natural',
      description: 'Áridos vistos que se integran perfectamente con el entorno',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Drenaje Perfecto',
      description: 'Superficie permeable que evita charcos y acumulación de agua',
    },
    {
      icon: 'ri-footprint-line',
      title: 'Antideslizante',
      description: 'Textura rugosa ideal para zonas húmedas y exteriores',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Alta Durabilidad',
      description: 'Resistente a la intemperie y al paso del tiempo',
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Variedad de Colores',
      description: 'Múltiples opciones de áridos y tonalidades',
    },
    {
      icon: 'ri-tools-line',
      title: 'Bajo Mantenimiento',
      description: 'No requiere tratamientos especiales ni sellados',
    },
  ];

  const applications = [
    {
      icon: 'ri-plant-line',
      title: 'Jardines y Parques',
      description: 'Caminos y senderos naturales',
    },
    {
      icon: 'ri-road-map-line',
      title: 'Aceras Peatonales',
      description: 'Vías urbanas y residenciales',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Alrededores de Piscinas',
      description: 'Zonas húmedas con drenaje',
    },
    {
      icon: 'ri-community-line',
      title: 'Plazas Públicas',
      description: 'Espacios comunitarios',
    },
    {
      icon: 'ri-car-line',
      title: 'Entradas de Vehículos',
      description: 'Accesos con acabado natural',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Patios y Terrazas',
      description: 'Espacios exteriores residenciales',
    },
  ];

  const benefits = [
    'Acabado natural con áridos vistos',
    'Excelente drenaje del agua',
    'Superficie antideslizante y segura',
    'Se integra con el paisaje natural',
    'Alta resistencia a la intemperie',
    'Variedad de colores y texturas',
    'Mantenimiento mínimo',
    'Durabilidad excepcional',
  ];

  const process = [
    {
      step: '1',
      title: 'Preparación',
      description: 'Nivelación y compactación del terreno base',
    },
    {
      step: '2',
      title: 'Vertido',
      description: 'Aplicación de hormigón con áridos seleccionados',
    },
    {
      step: '3',
      title: 'Desactivador',
      description: 'Aplicación de producto químico retardante en superficie',
    },
    {
      step: '4',
      title: 'Lavado',
      description: 'Eliminación de la capa superficial para dejar áridos vistos',
    },
    {
      step: '5',
      title: 'Acabado',
      description: 'Limpieza final y revisión de la superficie',
    },
  ];

  const isVisible = (id: string) => visibleSections.has(id);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-500 overflow-hidden"
        id="hero"
        data-animate
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className={`max-w-7xl mx-auto px-6 relative z-10 transition-all duration-1000 ${
          isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors cursor-pointer group"
              >
                <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
                <span>Volver a Servicios</span>
              </Link>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Hormigón Desactivado
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Pavimentos con áridos vistos que combinan belleza natural y funcionalidad. Ideal para jardines, caminos y zonas peatonales con acabado permeable y antideslizante.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+34640877479"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-teal-600 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  <span>Llamar Ahora</span>
                </a>
                <a
                  href="https://wa.me/34640877479"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {loading ? (
                <div className="col-span-2 flex items-center justify-center h-96">
                  <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                images.slice(0, 4).map((img, index) => (
                  <div
                    key={index}
                    className={`rounded-2xl overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500 ${
                      index === 0 ? 'col-span-2' : ''
                    } ${isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <div className="h-64">
                      <img
                        src={img}
                        alt={`Hormigón Desactivado ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        className="py-20 bg-gray-50"
        id="features"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Características Principales
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              El hormigón desactivado ofrece belleza natural y funcionalidad
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 group ${
                  isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-dark mb-4 group-hover:text-teal-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section 
        className="py-20 bg-white"
        id="applications"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('applications') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Aplicaciones Ideales
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Perfectos para espacios exteriores y naturales
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applications.map((app, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-100 hover:border-teal-500 transition-all duration-500 group ${
                  isVisible('applications') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-all duration-300">
                  <i className={`${app.icon} text-2xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-teal-600 transition-colors">
                  {app.title}
                </h3>
                <p className="text-secondary text-sm">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50"
        id="benefits"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${
              isVisible('benefits') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}>
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-8">
                Beneficios del Hormigón Desactivado
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 ${
                      isVisible('benefits') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                    }`}
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <p className="text-dark font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {loading ? (
                <div className="col-span-2 flex items-center justify-center h-96">
                  <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                images.slice(4, 8).map((img, index) => (
                  <div
                    key={index}
                    className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 ${
                      isVisible('benefits') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <div className="h-48">
                      <img
                        src={img}
                        alt={`Beneficio ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        className="py-20 bg-white"
        id="process"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('process') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Proceso de Instalación
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Trabajamos con precisión en cada etapa para garantizar resultados perfectos
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item, index) => (
              <div
                key={index}
                className={`relative transition-all duration-1000 ${
                  isVisible('process') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-gradient-to-br from-teal-500 to-cyan-500 p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group h-full">
                  <div className="text-5xl font-bold text-white/20 mb-4 group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-white/90 text-sm leading-relaxed">{item.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-teal-500 to-cyan-500 transform -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section 
        className="py-20 bg-gray-50"
        id="gallery"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('gallery') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Galería de Proyectos
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Descubre nuestros trabajos realizados en hormigón desactivado
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-secondary">Cargando galería...</p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {images.slice(8).map((img, index) => (
                <div
                  key={index}
                  className={`break-inside-avoid group transition-all duration-700 cursor-pointer ${
                    isVisible('gallery') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                  onClick={() => setSelectedImage(img)}
                >
                  <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500">
                    <div className="h-80">
                      <img
                        src={img}
                        alt={`Proyecto ${index + 1}`}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <p className="text-lg font-bold">Hormigón Desactivado</p>
                        <p className="text-sm opacity-90">Costa del Sol</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-500 relative overflow-hidden"
        id="cta"
        data-animate
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className={`max-w-4xl mx-auto px-6 text-center relative z-10 transition-all duration-1000 ${
          isVisible('cta') ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para tu Proyecto?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Solicita tu presupuesto gratuito y sin compromiso. Nuestro equipo te asesorará en todo el proceso.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+34640877479"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-teal-600 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>640 877 479</span>
            </a>
            <a
              href="https://wa.me/34640877479"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              <span>WhatsApp</span>
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-3 px-8 py-4 bg-teal-700 text-white rounded-full font-semibold hover:bg-teal-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>Formulario</span>
            </Link>
          </div>
        </div>
      </section>

      <QuoteFormSection />
      <Footer />
      <WhatsAppButton />
      <ScrollToTop />

      {/* Modal de Imagen */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={selectedImage}
            alt="Vista ampliada"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default HormigonDesactivadoPage;
